import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
  /** Смена значения сбрасывает пойманную ошибку: обычно это текущий адрес. */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

/**
 * Границы ошибок вокруг страницы. Нужны из-за ленивой загрузки: если чанк не
 * скачался (сеть отвалилась, выкатили новую версию и старый файл исчез),
 * без этого гаснет всё приложение, а не один раздел.
 */
class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Ошибка в разделе:', error, info.componentStack);
  }

  componentDidUpdate(prevProps: Props) {
    // Без сброса пойманная ошибка держится до перезагрузки страницы: уйти на
    // другой раздел и вернуться не помогает, экран остаётся заглушкой.
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-4 text-center">
        <h1 className="text-xl font-semibold text-foreground">Раздел не загрузился</h1>
        <p className="max-w-md text-sm text-muted-foreground">
          Обычно это значит, что вышла новая версия системы или пропала связь.
          Обновите страницу — если не поможет, сообщите администратору.
        </p>
        {/* В разработке показываем саму ошибку: иначе причину видно только в
            консоли, а по одному общему тексту её не найти */}
        {import.meta.env.DEV && (
          <pre className="max-w-full overflow-auto rounded bg-muted p-3 text-left text-xs text-muted-foreground">
            {error.message}
          </pre>
        )}
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={() => this.setState({ error: null })}>
            Попробовать снова
          </Button>
          <Button type="button" onClick={() => window.location.reload()}>
            Обновить страницу
          </Button>
        </div>
      </div>
    );
  }
}

/** Оборачивает границу ошибок, чтобы уход на другой адрес её сбрасывал. */
export default function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>;
}
